import React, { useState, useEffect, useCallback } from 'react';
import { GameProvider } from './context/GameContext';
import { useGame } from './context/GameContext';
import { useAuth } from './hooks/useAuth';
import GameBoard from './components/GameBoard';
import GameHeader from './components/GameHeader';
import InstructionsModal from './components/InstructionsModal';
import CompletionModal from './components/CompletionModal';
import SettingsModal from './components/SettingsModal';
import ModernGameControls from './components/ModernGameControls';
import ConfirmConnectionModal from './components/ConfirmConnectionModal';
import AdminPuzzleModal from './components/AdminPuzzleModal';
import AuthButton from './components/AuthButton';
import Toast from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import TutorialModal from './components/TutorialModal';
import CompatibilityWarning from './components/CompatibilityWarning';
import { getTodaysPuzzle } from './services/puzzleService';
import { saveCompletionShown, hasCompletionBeenShown } from './services/gameStateService';
import { isTutorialDismissed, dismissTutorial } from './services/tutorialService';

type Page = 'game' | 'completion' | 'settings' | 'instructions' | 'admin';

const GameApp: React.FC = () => {
  const { gameState, actions } = useGame();
  const { signInAnonymous, isAuthenticated, loading } = useAuth();
  const [currentPage, setCurrentPage] = useState<Page>('game');
  const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [isInitialized, setIsInitialized] = useState(false);
  const [isGameReady, setIsGameReady] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [hasCheckedTutorial, setHasCheckedTutorial] = useState(false);

  // Auto-sign in anonymously if not authenticated (but wait for Firebase to load)
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      signInAnonymous().catch(() => {});
    }
  }, [loading, isAuthenticated, signInAnonymous]);

  // Initialize game once auth is ready
  useEffect(() => {
    if (!isInitialized && !loading && gameState.wordsInOrder.length === 0) {
      setIsInitialized(true);
      const initGame = async () => {
        try {
          const wordList = await getTodaysPuzzle();
          actions.initializeGame(wordList);
          await actions.loadGameState();
        } catch (error) {
          // Fallback handled within getTodaysPuzzle, but adding extra safety
          actions.initializeGame(['Book', 'Page', 'Leaf', 'Tree', 'Wood', 'Log', 'Record', 'Album']);
          await actions.loadGameState();
        } finally {
          setIsGameReady(true);
        }
      };
      initGame();
    }
  }, [isInitialized, loading, gameState.wordsInOrder.length, actions]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effect to show tutorial on first visit
  useEffect(() => {
    if (!hasCheckedTutorial && isInitialized) {
      setHasCheckedTutorial(true);
      if (!isTutorialDismissed()) {
        setShowTutorial(true);
      }
    }
  }, [hasCheckedTutorial, isInitialized]);

  // Set up navigation callback for solution animation
  useEffect(() => {
    if (actions.setNavigationCallback) {
      actions.setNavigationCallback(() => {
        setCurrentPage('completion');
        saveCompletionShown();
      });
    }
  }, [actions]);

  // Effect to show completion page when game is completed (only once per day)
  // NOTE: This is now handled by the solution animation flow
  // useEffect(() => {
  //   if (gameState.gameCompleted && currentPage !== 'completion') {
  //     // Only auto-navigate if completion hasn't been shown today
  //     if (!hasCompletionBeenShown()) {
  //       setCurrentPage('completion');
  //       saveCompletionShown(); // Mark completion as shown for today
  //     }
  //   }
  // }, [gameState.gameCompleted, currentPage]);

  const handleRestartGame = useCallback(async () => {
    try {
      const wordList = await getTodaysPuzzle();
      actions.initializeGame(wordList);
      setCurrentPage('game'); // Go back to game page
    } catch (error) {
      actions.resetGame();
      setCurrentPage('game'); // Go back to game page even on error
    }
  }, [actions]);

  const handleShowTutorial = useCallback(() => {
    setShowTutorial(true);
  }, []);

  const handleCloseTutorial = useCallback(() => {
    setShowTutorial(false);
  }, []);

  const handleNeverShowTutorialAgain = useCallback(() => {
    dismissTutorial();
  }, []);

  const renderPage = () => {
    switch (currentPage) {
      case 'game':
        return (
          <>
            <div className="h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 relative flex flex-col">
              {/* Header */}
              <div className="flex-shrink-0">
                <GameHeader 
                  onShowSettings={() => setCurrentPage('settings')}
                  onShowAdminModal={() => setShowAdminModal(true)}
                  onShowHelp={handleShowTutorial}
                />
              </div>

              {/* Game Board */}
              {isGameReady && (
                <div className="flex-1 relative">
                  <GameBoard width={dimensions.width} height={dimensions.height - 40} />
                </div>
              )}

              {/* Modern Floating Controls */}
              {isGameReady && (
                <ModernGameControls 
                  onShowSettings={() => setCurrentPage('settings')}
                  onShowInstructions={() => setCurrentPage('instructions')}
                  onShowCompletion={() => setCurrentPage('completion')}
                />
              )}

              {/* Auth Button */}
              <AuthButton />


              {/* Confirmation Modal */}
              <ConfirmConnectionModal />

              
              {/* Admin Modal */}
              <AdminPuzzleModal 
                isOpen={showAdminModal}
                onClose={() => setShowAdminModal(false)}
              />
            </div>
          </>
        );
      case 'completion':
        return (
          <CompletionModal
            isOpen={true} // Always open when this page is active
            onClose={() => setCurrentPage('game')}
            onRestart={handleRestartGame}
          />
        );
      case 'settings':
        return (
          <SettingsModal
            isOpen={true} // Always open when this page is active
            onClose={() => setCurrentPage('game')}
          />
        );
      case 'instructions':
        return (
          <InstructionsModal
            isOpen={true} // Always open when this page is active
            onClose={() => setCurrentPage('game')}
          />
        );
      case 'admin':
        return (
          <AdminPuzzleModal
            isOpen={true}
            onClose={() => setCurrentPage('game')}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Toast 
        message={gameState.toast.message} 
        show={gameState.toast.show} 
        type={gameState.toast.type} 
      />
      <TutorialModal 
        isOpen={showTutorial}
        onClose={handleCloseTutorial}
        onNeverShowAgain={handleNeverShowTutorialAgain}
      />
      <CompatibilityWarning />
      {renderPage()}
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <GameProvider>
        <GameApp />
      </GameProvider>
    </ErrorBoundary>
  );
}

export default App;